/**
 * Safe reply renderer for the help assistant (D10-SEC-005, #3604).
 *
 * Replies are untrusted model output, so this builds React elements only --
 * never raw HTML. It understands a deliberately small Markdown subset:
 *
 *  - paragraphs (blank-line separated) and single line breaks
 *  - `**bold**`, `*italic*`, inline `` `code` ``
 *  - `-` / `*` bullet lists and `1.` numbered lists
 *  - `[label](/route)` -- a React Router link ONLY when `/route` is one of the
 *    user's allowed in-app routes; anything else (external `http(s):`,
 *    `javascript:`, `data:`, protocol-relative `//`, unknown routes) renders as
 *    its plain label with no link
 *
 * Everything else -- HTML tags, entities, images (`![a](b)`) -- renders as the
 * literal text it is, because React escapes every string it is given.
 *
 * The same renderer draws a reply while it is still streaming (#3675), with
 * `partial: true`: only the LAST line -- the one still being typed -- is
 * settled first, so a half-typed marker never flashes as raw punctuation. An
 * unclosed `**bold`, `*italic` or `` `code `` renders styled as if closed, an
 * empty opener (`**`, a lone `-` or `2.` list marker) is held back, and a
 * half-typed `[label](...` shows just its label, never a link. Settling only
 * ever adds a closing marker or drops syntax, so the output is exactly as safe
 * as a finished reply, and once the real closing marker arrives the text looks
 * the same -- nothing changes style when the reply completes.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface RenderReplyOptions {
  /** In-app routes a link may point at (the user's allowed_routes). */
  allowedRoutes: ReadonlySet<string> | readonly string[];
  /** Called when the user follows an in-app link (e.g. to close the panel). */
  onLinkClick?: () => void;
  /** The reply is still streaming: settle a half-typed marker on its last line. */
  partial?: boolean;
}

const BULLET = /^\s*[-*]\s+(.*)$/;
const NUMBERED = /^\s*\d{1,3}[.)]\s+(.*)$/;

// Order matters: an image must win over the link it contains, code over the
// emphasis markers inside it, bold over italic.
const INLINE =
  /(!\[[^\]\n]*\]\([^)\n]*\))|`([^`\n]+)`|\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|\[([^\]\n]+)\]\(([^)\s]*)\)/g;

const LIST_PREFIX = /^\s*(?:[-*]|\d{1,3}[.)])\s+/;
const LONE_LIST_MARKER = /^\s*(?:[-*]|\d{1,3}[.)])\s*$/;
// A link still being typed at the very end of the line: `[lab`, `[label]`,
// `[label](/ro`. An image (`![...`) is left alone -- it stays literal text.
const OPEN_LINK = /(^|[^!])\[([^\]\n]*)(?:\](?:\([^)\s]*)?)?$/;

// Complete spans whose contents are never emphasis markup: an image, inline
// code, a link. They are blanked out before counting openers.
const OPAQUE_SPAN = /!\[[^\]\n]*\]\([^)\n]*\)|`[^`\n]+`|\[[^\]\n]+\]\([^)\s]*\)/g;
const FILLER = "x";

// Any complete link, to show only its label where markup is literal text.
const ANY_LINK = /\[([^\]\n]+)\]\(([^)\s]*)\)/g;

/**
 * Text that is shown literally (inline code, an image) with every link in it
 * reduced to its label (#4599): a link's target is a web path, and a raw path
 * is never shown to the user, even inside a code span or a broken image.
 */
function withoutLinkTargets(text: string): string {
  return text.replace(ANY_LINK, (_m, label: string) => label);
}

// An image's target runs to the first `)`, so it can swallow a link whose own
// `)` then closes the image: `![a](b c [Training](/crm/training)`.
const NESTED_LINK = /\[([^\]\n]+)\]\([^)\s]*\)?/g;

/** An image, shown literally, with any link typed inside its target reduced to its label. */
function literalImage(whole: string): string {
  const at = whole.indexOf("](");
  if (at < 0) return whole;
  const head = whole.slice(0, at + 2);
  const rest = whole.slice(at + 2);
  return head + rest.replace(NESTED_LINK, (_m, label: string) => label);
}

function count(haystack: string, needle: string): number {
  return haystack.split(needle).length - 1;
}

/** Blank out complete code spans, links and images, keeping every offset. */
function maskOpaqueSpans(text: string): string {
  return text.replace(OPAQUE_SPAN, (span) => FILLER.repeat(span.length));
}

/**
 * Settle the line a streaming reply is still typing, so a half-typed marker
 * renders as it will once closed (or not at all) instead of as raw syntax.
 *
 * Openers are found across the WHOLE line, with complete code spans and links
 * masked out, so a bold or italic that wraps a finished link or code span
 * (`**Open [Deals](/crm/deals) no`) is still closed.
 */
function settleOpenLine(line: string): string {
  if (LONE_LIST_MARKER.test(line)) return "";
  const prefix = line.match(LIST_PREFIX)?.[0] ?? "";
  let body = line.slice(prefix.length);
  let masked = maskOpaqueSpans(body);
  // `**bold*` is a bold whose closing marker is half-arrived, not an italic.
  if (count(masked, "**") % 2 === 1 && /[^*]\*$/.test(masked)) {
    body += "*";
    masked += "*";
  }

  // Emphasis openers only count before an unclosed code span: inside inline
  // code nothing else is markup.
  let end = body.length;
  let codeCloser = "";
  if (count(masked, "`") % 2 === 1) {
    const at = masked.lastIndexOf("`");
    if (at === body.length - 1) {
      body = body.slice(0, at);
      masked = masked.slice(0, at);
    } else {
      codeCloser = "`";
    }
    end = at;
  } else {
    const link = masked.match(OPEN_LINK);
    if (link) {
      const start = (link.index ?? 0) + (link[1] ?? "").length;
      // The mask keeps offsets, so the label is read back from the real text.
      const label = body.slice(start + 1, start + 1 + (link[2] ?? "").length);
      body = body.slice(0, start) + label;
      masked = masked.slice(0, start) + FILLER.repeat(label.length);
      end = body.length;
    }
  }

  const region = masked.slice(0, end);
  let emphasisCloser = "";
  if (count(region, "**") % 2 === 1) {
    const at = region.lastIndexOf("**");
    const inner = body.slice(at + 2).replace(/\*$/, "");
    if (!inner.trim()) {
      body = body.slice(0, at);
      codeCloser = "";
    } else if (/^\S/.test(inner)) {
      emphasisCloser = "**";
    }
  } else if (count(region, "*") % 2 === 1) {
    const at = region.lastIndexOf("*");
    const inner = body.slice(at + 1);
    if (!inner) {
      body = body.slice(0, at);
      codeCloser = "";
    } else if (/^\S/.test(inner)) {
      emphasisCloser = "*";
    }
  }
  return prefix + body + codeCloser + emphasisCloser;
}

function normaliseRoute(path: string): string {
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

/** True only for a same-app absolute path that is in the allowed list. */
export function isAllowedRoute(
  href: string,
  allowed: ReadonlySet<string> | readonly string[],
): boolean {
  if (!href.startsWith("/") || href.startsWith("//")) return false;
  // No scheme, backslash, query, fragment or whitespace tricks: exact paths only.
  if (/[:\\?#\s]/.test(href)) return false;
  const target = normaliseRoute(href);
  const list: Iterable<string> = allowed;
  for (const route of list) {
    if (normaliseRoute(route) === target) return true;
  }
  return false;
}

function renderInline(text: string, opts: RenderReplyOptions, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const start = m.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    const key = `${keyPrefix}-${n++}`;
    const [whole, image, code, bold, italic, label, href] = m;
    if (image !== undefined) {
      out.push(literalImage(whole));
    } else if (code !== undefined) {
      out.push(
        <code
          key={key}
          className="rounded bg-[var(--color-bg-page)] px-1 py-0.5 font-mono text-[0.85em]"
        >
          {withoutLinkTargets(code)}
        </code>,
      );
    } else if (bold !== undefined) {
      out.push(<strong key={key}>{renderInline(bold, opts, key)}</strong>);
    } else if (italic !== undefined) {
      out.push(<em key={key}>{renderInline(italic, opts, key)}</em>);
    } else if (label !== undefined) {
      if (href !== undefined && isAllowedRoute(href, opts.allowedRoutes)) {
        out.push(
          <Link
            key={key}
            to={normaliseRoute(href)}
            onClick={opts.onLinkClick}
            className="font-medium text-[var(--color-accent)] underline underline-offset-2 hover:text-[var(--color-accent-hover)]"
          >
            {label}
          </Link>,
        );
      } else {
        out.push(label);
      }
    }
    last = start + whole.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] };

function toBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | null = null;
  for (const line of text.split("\n")) {
    if (!line.trim()) {
      current = null;
      continue;
    }
    const bullet = line.match(BULLET);
    const numbered = bullet ? null : line.match(NUMBERED);
    if (bullet) {
      if (current?.kind !== "ul") {
        current = { kind: "ul", items: [] };
        blocks.push(current);
      }
      current.items.push(bullet[1] ?? "");
    } else if (numbered) {
      if (current?.kind !== "ol") {
        current = { kind: "ol", items: [] };
        blocks.push(current);
      }
      current.items.push(numbered[1] ?? "");
    } else {
      if (current?.kind !== "p") {
        current = { kind: "p", lines: [] };
        blocks.push(current);
      }
      current.lines.push(line);
    }
  }
  return blocks;
}

/** Render an assistant reply as safe React elements. */
export function renderReply(text: string, opts: RenderReplyOptions): ReactNode {
  let source = text.replace(/\r\n?/g, "\n");
  if (opts.partial) {
    const cut = source.lastIndexOf("\n") + 1;
    source = source.slice(0, cut) + settleOpenLine(source.slice(cut));
  }
  return toBlocks(source).map((block, i) => {
    const key = `b${i}`;
    if (block.kind === "p") {
      return (
        <p key={key} className="[&:not(:first-child)]:mt-2">
          {block.lines.map((line, j) => (
            <span key={`${key}-${j}`}>
              {j > 0 && <br />}
              {renderInline(line, opts, `${key}-${j}`)}
            </span>
          ))}
        </p>
      );
    }
    const items = block.items.map((item, j) => (
      <li key={`${key}-${j}`}>{renderInline(item, opts, `${key}-${j}`)}</li>
    ));
    return block.kind === "ul" ? (
      <ul key={key} className="list-disc space-y-1 pl-5 [&:not(:first-child)]:mt-2">
        {items}
      </ul>
    ) : (
      <ol key={key} className="list-decimal space-y-1 pl-5 [&:not(:first-child)]:mt-2">
        {items}
      </ol>
    );
  });
}
